import React, { useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import config from "../config";
import announcementIllustration from "assets/images/important-announcement.png";
import { dismissFeatureAnnouncement } from "store/slices/featureAnnouncementSlice";
import "./FeatureAnnouncementModal.css";

const FeatureAnnouncementModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const currentUser = auth.user;
  const isOpen = useSelector((state) => state.featureAnnouncement.isOpen);
  const announcement = useSelector((state) => state.featureAnnouncement.currentAnnouncement);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  React.useEffect(() => {
    setDontShowAgain(false);
  }, [announcement?.id]);

  if (!isOpen || !announcement) {
    return null;
  }

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const dismissedForever = dontShowAgain && announcement.allow_dismiss_forever;
    dispatch(
      dismissFeatureAnnouncement(announcement, currentUser?.id, dismissedForever, true)
    );
    setDontShowAgain(false);
  };

  const handleAction = () => {
    const path = announcement.action_url?.trim();
    const dismissedForever = dontShowAgain && announcement.allow_dismiss_forever;
    dispatch(
      dismissFeatureAnnouncement(announcement, currentUser?.id, dismissedForever, false)
    );
    setDontShowAgain(false);
    if (path) {
      const target = path.startsWith("/") ? config.basePath + path : path;
      navigate(target);
    }
  };

  const modal = (
    <div
      className="feature-announcement-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feature-announcement-title"
    >
      <div className="modal-dialog">
        <div className="modal-content feature-announcement-card">
          <div className="feature-announcement-hero">
            <img
              src={announcementIllustration}
              alt={t("Important announcement")}
              className="feature-announcement-hero__img"
            />
            <button
              type="button"
              className="feature-announcement-close"
              aria-label={t("Close")}
              onClick={handleClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="feature-announcement-message">
            {announcement.feature_key ? (
              <span className="feature-announcement-badge">{announcement.feature_key}</span>
            ) : null}
            <h5 id="feature-announcement-title" className="feature-announcement-title">
              {announcement.title}
            </h5>
            {announcement.summary ? (
              <p className="feature-announcement-summary">{announcement.summary}</p>
            ) : null}
            {announcement.body ? (
              <div
                className="feature-announcement-body"
                dangerouslySetInnerHTML={{ __html: announcement.body }}
              />
            ) : null}
          </div>

          <div className="feature-announcement-actions">
            {announcement.allow_dismiss_forever ? (
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="feature-announcement-dont-show"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="feature-announcement-dont-show">
                  {t("Don't show this again")}
                </label>
              </div>
            ) : null}
            <div className="d-flex flex-wrap gap-2 justify-content-end">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClose}>
                {t("Close")}
              </button>
              {announcement.action_label && announcement.action_url ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={handleAction}>
                  {announcement.action_label}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

FeatureAnnouncementModal.propTypes = {
  announcement: PropTypes.object,
};

export default FeatureAnnouncementModal;
